import React, { useEffect, useState } from 'react';
import {
  DataGridPro,
  GridColDef,
  GridOverlay,
  GridFilterModel,
  GridSortModel,
  getGridDateOperators,
} from '@mui/x-data-grid-pro';
import IconButton from '@mui/material/IconButton';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';

import { CompanyCertification } from '../../interfaces/users.interface';
import GridPaginationBar from './GridPaginationBar';
import {
  getCompanyCertificationsForDataGrid,
  updateCompanyCertificationApproval,
  updateCompanyCertificatioReviewed,
} from '../../services/companyCertifications';
import { Card, Chip, Switch } from '@mui/material';
import { useAppSelector } from '../../app/hooks';
import { Link } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { blueGrey, green, grey, orange } from '@mui/material/colors';
import { USER_TYPES } from '../../services/user';
import { formatDate, normalizeFilterModel, stringContainsOnly } from '../../utils/adminDashboardUtils';
import {
  CurrentScreeningSummary,
  ScreeningRun,
  ScreeningState,
  screeningStateFor,
} from '../../interfaces/screening.interface';
import { getScreeningRun } from '../../services/screening';
import { SCREENING_STATE_LABELS } from './screeningLabels';
import { ScreeningStateFilter } from './ScreeningFilters';
import ScreeningRunDetailDialog from './Modals/ScreeningRunDetailDialog';
import RescanCertificateDialog from './Modals/RescanCertificateDialog';

interface CertificationRow {
  id: number | undefined;
  companyName: string | undefined;
  companyId: number | undefined;
  expirationDate?: string | Date;
  adminApproved: boolean;
  adminReviewed: boolean;
  certification: string;
  version: string;
  releaseDate?: string | Date;
  document: string;
  createdAt?: string | Date;
  currentScreening: CurrentScreeningSummary | null;
  screeningEnabled: boolean;
}

function createRowLink(certRow: CertificationRow) {
  return (
    <a href={certRow.document} target="_blank" rel="noreferrer">
      <IconButton size="small" name="View Certification">
        <LinkOutlinedIcon />
      </IconButton>
    </a>
  );
}

const SCREENING_CHIP_COLORS: Record<ScreeningState, string | undefined> = {
  not_screened: undefined,
  needs_review: orange[700],
  reviewed: blueGrey[400],
  not_evaluated: grey[600],
  clean: green[600],
};

/**
 * The Screening column's cell. Flag counts lead the label, because "how many things does
 * this certificate need me to look at" is the question the column exists to answer.
 */
function screeningChip(summary: CurrentScreeningSummary | null) {
  const state = screeningStateFor(summary);
  const background = SCREENING_CHIP_COLORS[state];

  let label = SCREENING_STATE_LABELS[state];
  if (state === 'needs_review') {
    label = summary?.unresolvedFlagCount === 1 ? '1 Flag' : `${summary?.unresolvedFlagCount} Flags`;
  } else if (state === 'reviewed') {
    label = summary?.flagCount === 1 ? '1 Flag Reviewed' : `${summary?.flagCount} Flags Reviewed`;
  }

  return (
    <Chip
      size="small"
      label={label}
      variant={background ? 'filled' : 'outlined'}
      sx={background ? { backgroundColor: background, color: '#fff' } : undefined}
    />
  );
}

function createCertificationRow(companyCert: CompanyCertification): CertificationRow {
  return {
    id: companyCert.id,
    companyId: companyCert.company,
    companyName: companyCert.companyName,
    expirationDate: companyCert.expirationDate,
    adminApproved: !!companyCert.adminApproved,
    adminReviewed: !!companyCert.adminReviewed,
    certification: companyCert.certification.title,
    version: companyCert.certification.version || '-',
    releaseDate: companyCert.certification.releaseDate,
    document: companyCert.document,
    createdAt: companyCert.createdAt,
    currentScreening: companyCert.currentScreening ?? null,
    screeningEnabled: !!companyCert.certification.screeningEnabled,
  };
}

export default function CompanyCertificationAdminPanel() {
  const [rows, setRows] = useState<CertificationRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<CertificationRow | null>(null);
  const [detailRun, setDetailRun] = useState<ScreeningRun | null>(null);
  const [loadingRunId, setLoadingRunId] = useState<number | null>(null);
  const [rescanRow, setRescanRow] = useState<CertificationRow | null>(null);

  const { user } = useAppSelector(state => state.user);
  const userIsAdmin: boolean = user?.type === USER_TYPES.TPN_ADMIN;

  const fetchAndSetRows = async () => {
    try {
      const data = await getCompanyCertificationsForDataGrid({
        page,
        pageSize,
        sortModel,
        filterModel,
      });

      const mapped = data.results.map(createCertificationRow);
      setRows(mapped);
      setRowCount(data.count);
    } catch (err) {
      console.error('Error fetching company certifications', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchAndSetRows();
  }, [page, pageSize, sortModel, filterModel]);

  // The switches are controlled off `rows`, so the local row has to move with the write —
  // otherwise flipping the same flag from the screening detail dialog and from the grid
  // leaves the two showing different values.
  const patchRow = (id: number, changes: Partial<CertificationRow>) => {
    setRows(prev => prev.map(row => (row.id === id ? { ...row, ...changes } : row)));
  };

  const handleApprovedChange = (row: CertificationRow) => async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!row.id) return;
    const checked = event.target.checked;
    patchRow(row.id, { adminApproved: checked });
    try {
      await updateCompanyCertificationApproval(row.id, checked);
    } catch (err) {
      console.error('Error updating certification approval', err);
      patchRow(row.id, { adminApproved: !checked });
    }
  };

  const handleReviewedChange = (row: CertificationRow) => async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!row.id) return;
    const checked = event.target.checked;
    patchRow(row.id, { adminReviewed: checked });
    try {
      await updateCompanyCertificatioReviewed(row.id, checked);
    } catch (err) {
      console.error('Error updating certification reviewed status', err);
      patchRow(row.id, { adminReviewed: !checked });
    }
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const handleViewScreening = async () => {
    const summary = menuRow?.currentScreening;
    closeMenu();
    if (!summary) return;

    setLoadingRunId(summary.id);
    try {
      setDetailRun(await getScreeningRun(summary.id));
    } catch (err) {
      console.error('Error fetching screening run', err);
    } finally {
      setLoadingRunId(null);
    }
  };

  const handleRescanRequested = () => {
    setRescanRow(menuRow);
    closeMenu();
  };

  const handleDetailClosed = () => {
    setDetailRun(null);
    // The dialog can change adminApproved/adminReviewed and accept/dismiss flags, all of
    // which the grid renders.
    fetchAndSetRows();
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
      filterOperators: stringContainsOnly,
    },
    {
      field: 'company__name',
      headerName: 'Company Name',
      filterOperators: stringContainsOnly,
      width: 230,
      renderCell: params => (
        <Link to={`/registry/${params.row.companyId}`}>
          {params.row.companyName}
        </Link>
      ),
    },
    {
      field: 'certification__title',
      headerName: 'Certification',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.certification,
      valueFormatter: params => params.value,

    },
    {
      field: 'certification__version',
      headerName: 'Version',
      filterOperators: stringContainsOnly,
      width: 130,
      valueGetter: params => params.row.version,
      valueFormatter: params => params.value,
    },
    {
      field: 'created_at',
      headerName: 'Upload Date',
      type: 'dateTime',
      filterOperators: getGridDateOperators().filter(op => op.value === 'is'),
      width: 130,
      valueGetter: params => formatDate(params.row.createdAt),
      renderCell: params => formatDate(params.row.createdAt),
    },
    {
      field: 'certification__release_date',
      headerName: 'Release Date',
      type: 'dateTime',
      filterOperators: getGridDateOperators().filter(op => op.value === 'is'),
      width: 130,
      valueGetter: params => formatDate(params.row.releaseDate),
      renderCell: params => formatDate(params.row.releaseDate),
    },
    {
      field: 'expiration_date',
      headerName: 'Expiration Date',
      type: 'dateTime',
      filterOperators: getGridDateOperators().filter(op => op.value === 'is'),
      width: 130,
      valueGetter: params => formatDate(params.row.expirationDate),
      renderCell: params => formatDate(params.row.expirationDate),
    },
    {
      field: 'adminApproved',
      headerName: 'Admin Approved',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <Switch
          id={`cert-approved-${params.row.id}`}
          name={`cert-approved-${params.row.id}`}
          disabled={!userIsAdmin}
          checked={params.row.adminApproved}
          onChange={handleApprovedChange(params.row as CertificationRow)}
        />
      ),
    },
    {
      field: 'adminReviewed',
      headerName: 'Admin Reviewed',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <Switch
          id={`cert-reviewed-${params.row.id}`}
          name={`cert-reviewed-${params.row.id}`}
          disabled={!userIsAdmin}
          checked={params.row.adminReviewed}
          onChange={handleReviewedChange(params.row as CertificationRow)}
        />
      ),
    },
    {
      field: 'link',
      headerName: 'Link',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: params => createRowLink(params.row),
    },
  ];

  // Screening is a TPN Admin-only review tool, and the API omits `currentScreening`
  // entirely for everyone else — so the column and its menu actions only exist for admins.
  const screeningColumns: GridColDef[] = userIsAdmin ? [
    {
      field: 'screening_state',
      headerName: 'Screening',
      width: 160,
      sortable: false,
      valueGetter: params => SCREENING_STATE_LABELS[screeningStateFor(params.row.currentScreening)],
      renderCell: params => (params.row.id === loadingRunId
        ? <CircularProgress size={20} />
        : screeningChip(params.row.currentScreening)),
      filterOperators: [{
        label: 'Is',
        value: 'is',
        getApplyFilterFn: () => null,
        InputComponent: ScreeningStateFilter,
      }],
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <IconButton
          size="small"
          aria-label="Certificate actions"
          onClick={(event) => {
            setMenuAnchor(event.currentTarget);
            setMenuRow(params.row as CertificationRow);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ] : [];

  return (
    <Card sx={{ padding: '2em' }}>
      <DataGridPro
        style={{ height: 400, width: '100%' }}
        rows={rows}
        columns={[...columns, ...screeningColumns]}
        columnBuffer={columns.length + screeningColumns.length}
        pagination
        paginationMode="server"
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={[25, 50, 100, 200]}
        rowCount={rowCount}
        onPageChange={newPage => {
          setPage(newPage);
        }}
        onPageSizeChange={newPageSize => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={model => {
          setPage(0);
          setSortModel(model);
        }}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={(model) => {
          const normalized = normalizeFilterModel(model);
          const prev = JSON.stringify(filterModel.items ?? []);
          const next = JSON.stringify(normalized.items ?? []);

          if (prev === next) return;

          setPage(0);
          setFilterModel(normalized);
        }}
        loading={isLoading}
        components={{
          Footer: GridPaginationBar,
          LoadingOverlay: () => (
            <GridOverlay>
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <CircularProgress />
              </div>
            </GridOverlay>
          ),
        }}
      />

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem onClick={handleViewScreening} disabled={!menuRow?.currentScreening}>
          View Screening Result
        </MenuItem>
        <MenuItem onClick={handleRescanRequested} disabled={!menuRow?.screeningEnabled || !menuRow?.document}>
          {menuRow?.currentScreening ? 'Re-screen Certificate' : 'Screen Certificate'}
        </MenuItem>
      </Menu>

      <ScreeningRunDetailDialog
        isOpen={!!detailRun}
        run={detailRun}
        handleClose={handleDetailClosed}
        onRunUpdated={setDetailRun}
        onRequestRerun={() => {
          const run = detailRun;
          setDetailRun(null);
          setRescanRow(rows.find(row => row.id === run?.companyCertification) ?? null);
        }}
      />

      <RescanCertificateDialog
        isOpen={!!rescanRow}
        companyCertificationId={rescanRow?.id ?? null}
        label={`${rescanRow?.companyName ?? ''} — ${rescanRow?.certification ?? ''}`}
        isFirstScan={!rescanRow?.currentScreening}
        handleClose={() => setRescanRow(null)}
        onQueued={fetchAndSetRows}
      />
    </Card>
  );
}
